import React, { useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import Toast from "../common/Toast.jsx"
import V2PageContainer from "../common/V2PageContainer.jsx"
import ScrollToTopButton from "../common/ScrollToTopButton.jsx"
import useInfiniteApiList from "../hooks/useInfiniteApiList.js"
import useIsMobileViewport from "../hooks/useIsMobileViewport.js"
import useNotImplementedToast from "../hooks/useNotImplementedToast.js"
import { FIT_PER_PAGE_STORAGE_KEY, readCachedFitPerPage, writeCachedFitPerPage } from "../utils/fitPerPageStorage.js"
import { readCurrentYearFilterOn, writeCurrentYearFilterOn } from "../utils/currentYearFilterPref.js"
import { refetchRemainingMs } from "../utils/loading.js"
import ManifestsDesktopTable from "./ManifestsDesktopTable.jsx"
import ManifestsFilters from "./ManifestsFilters.jsx"
import ManifestsHeader from "./ManifestsHeader.jsx"
import ManifestsMobileList from "./ManifestsMobileList.jsx"

const MOBILE_PAGE_SIZE = 25

/**
 * @param {{
 *   app: any,
 *   api: any,
 *   helpers?: {
 *     formatStatus?: (status: any) => string,
 *     manifestStatusColor?: (status: any) => string,
 *   },
 *   showAllLocationsBadge?: boolean,
 *   showImportLodgeManifestWorkflow?: boolean,
 *   showCurrentYearFilter?: boolean,
 *   defaultCurrentYearFilterOn?: boolean | undefined,
 *   onCurrentYearToggled?: ((nextIsOn: boolean) => void) | undefined,
 *   currentYearFilterStorageKey?: string | undefined,
 *   columns?: Array<{
 *     key: string,
 *     label: any,
 *     preferredWidth?: number,
 *     minWidth?: number,
 *     cellType?: "text" | "status" | "custom",
 *     value?: (manifest: any) => any,
 *     render?: (manifest: any) => any,
 *     headerClassName?: string,
 *     headerStyle?: any,
 *     cellClassName?: string,
 *     cellStyle?: any,
 *   }>,
 * }} props
 */
export default function ManifestsPage({
    app,
    api,
    helpers,
    showAllLocationsBadge = true,
    showImportLodgeManifestWorkflow = true,
    showCurrentYearFilter = false,
    defaultCurrentYearFilterOn = undefined,
    onCurrentYearToggled = undefined,
    currentYearFilterStorageKey = undefined,
    columns = undefined,
}) {
    const { toastProps, showNotImplementedToast } = useNotImplementedToast()
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const [manifests, setManifests] = useState([])
    const [totalRows, setTotalRows] = useState(0)
    const [isLoadingInitialDesktop, setIsLoadingInitialDesktop] = useState(true)
    const [showDesktopRefetchLoader, setShowDesktopRefetchLoader] = useState(false)
    const [searchTerm, setSearchTerm] = useState(searchParams.get("search") ?? "")
    const [selectedManifests, setSelectedManifests] = useState(/** @type {any[]} */([]))
    const [fitPerPage, setFitPerPage] = useState(
        () => readCachedFitPerPage(FIT_PER_PAGE_STORAGE_KEY.manifests) ?? 25
    )
    const hasLoadedDesktopOnceRef = React.useRef(false)
    const desktopRequestIdRef = React.useRef(0)
    const desktopFetchInFlightRef = React.useRef(0)
    const isMobile = useIsMobileViewport()
    const pageParam = searchParams.get("page")
    const perPageParam = searchParams.get("per_page")
    const searchParam = searchParams.get("search")
    const locationParam = searchParams.get("location")
    const yearParam = searchParams.get("year")

    const formatStatus = helpers?.formatStatus ?? ((x) => String(x ?? ""))
    const manifestStatusColor = helpers?.manifestStatusColor

    function getPage() {
        const parsed = Number(pageParam)
        return Number.isInteger(parsed) && parsed > 0 ? parsed : 1
    }

    function getPerPage() {
        if (!perPageParam || (perPageParam ?? "").toLowerCase() === "fit") {
            return fitPerPage
        }
        const parsed = Number(perPageParam)
        return Number.isInteger(parsed) && parsed > 0 ? parsed : 25
    }

    function isPerPageFit() {
        return !perPageParam || (perPageParam ?? "").toLowerCase() === "fit"
    }

    function getSearch() {
        return searchParam ?? ""
    }

    function getLocationQuery() {
        if ((locationParam ?? "").toLowerCase() === "all") {
            return ""
        }
        if (getSearch()) {
            return ""
        }
        return app?.locationId
    }

    function getCurrentYear() {
        return String(new Date().getFullYear())
    }

    function getYear() {
        return yearParam ?? ""
    }

    useEffect(() => {
        const effectiveDefaultOn = (
            typeof defaultCurrentYearFilterOn === "boolean"
                ? defaultCurrentYearFilterOn
                : readCurrentYearFilterOn(currentYearFilterStorageKey)
        )
        if (!showCurrentYearFilter || !effectiveDefaultOn) {
            return
        }
        if (String(getYear()).trim() !== "") {
            return
        }
        const nextParams = new URLSearchParams(searchParams)
        nextParams.set("year", getCurrentYear())
        setSearchParams(nextParams)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showCurrentYearFilter, defaultCurrentYearFilterOn, currentYearFilterStorageKey, yearParam])

    function mapManifestForList(manifest, locations) {
        const store = locations.find((x) => x.id === manifest.location_id)
        const rawStatus = manifest.status
        return {
            id: manifest.id,
            location: store?.name ?? "",
            manifestNumber: manifest.manifest_num,
            numOrders: manifest.num_orders,
            status: formatStatus(rawStatus),
            rawStatus,
            createdDate: manifest.created_at?.split("T")?.[0] ?? "",
            closedDate: manifest.closed_at?.split("T")?.[0] ?? "—",
            shippedDate: manifest.shipped_at?.split("T")?.[0] ?? "—",
            // Backwards-compat alias (old column label in shared table).
            transferredDate: manifest.shipped_at?.split("T")?.[0] ?? "—",
            receivedDate: manifest.received_at?.split("T")?.[0] ?? "—",
        }
    }

    function handlePageChange(nextPage) {
        const nextParams = new URLSearchParams(searchParams)
        nextParams.set("page", String(nextPage))
        setSearchParams(nextParams)
    }

    function handlePerPageChange(nextPerPage) {
        const nextParams = new URLSearchParams(searchParams)
        if (nextPerPage === "fit") {
            nextParams.set("per_page", "fit")
        } else {
            nextParams.set("per_page", String(nextPerPage))
        }
        nextParams.set("page", "1")
        setSearchParams(nextParams)
    }

    function handleFitPerPageChange(nextFitPerPage) {
        setFitPerPage((current) => {
            if (!Number.isInteger(nextFitPerPage) || nextFitPerPage <= 0) {
                return current
            }
            if (current === nextFitPerPage) {
                return current
            }
            writeCachedFitPerPage(FIT_PER_PAGE_STORAGE_KEY.manifests, nextFitPerPage)
            return nextFitPerPage
        })
    }

    function handleSearchSubmit(event) {
        event.preventDefault()
        const nextParams = new URLSearchParams(searchParams)
        if (!searchTerm.trim()) {
            nextParams.delete("search")
        } else {
            nextParams.set("search", searchTerm.trim())
        }
        nextParams.set("page", "1")
        setSearchParams(nextParams)
    }

    function onRefreshClick() {
        window.location.reload()
    }

    function onToggleAllLocations() {
        const nextParams = new URLSearchParams(searchParams)
        if ((locationParam ?? "").toLowerCase() === "all") {
            nextParams.delete("location")
        } else {
            nextParams.set("location", "all")
        }
        nextParams.set("page", "1")
        setSearchParams(nextParams)
    }

    function onToggleCurrentYear() {
        const nextParams = new URLSearchParams(searchParams)
        const isCurrentlyOn = String(getYear()) === getCurrentYear()
        const nextIsOn = !isCurrentlyOn
        if (isCurrentlyOn) {
            nextParams.delete("year")
        } else {
            nextParams.set("year", getCurrentYear())
        }
        nextParams.set("page", "1")
        setSearchParams(nextParams)
        writeCurrentYearFilterOn(nextIsOn, currentYearFilterStorageKey)
        if (typeof onCurrentYearToggled === "function") {
            onCurrentYearToggled(nextIsOn)
        }
    }

    function handleManifestClick(manifest) {
        navigate(`/v2/manifests/${manifest.id}`)
    }

    useEffect(() => {
        setSearchTerm(searchParam ?? "")
    }, [searchParam])

    const mobileManifestsList = useInfiniteApiList({
        enabled: Boolean(app?.loaded && isMobile),
        perPage: MOBILE_PAGE_SIZE,
        deps: [app?.locationId, searchParam, locationParam, yearParam],
        fetchPage: async (page, perPage) => {
            const locations = /** @type {any[]} */ (app?.locations ?? [])
            const body = await api.getManifests(
                page,
                perPage,
                "created_at",
                "desc",
                getSearch(),
                /** @type {any} */ (getLocationQuery()),
                getYear()
            )
            return {
                items: (body?.data ?? []).map((manifest) => (
                    mapManifestForList(manifest, locations)
                )),
                total: body?.total ?? 0,
            }
        },
    })

    useEffect(() => {
        if (!app?.loaded) {
            return
        }
        if (isMobile) {
            return
        }

        const locations = /** @type {any[]} */ (app?.locations ?? [])
        const isRefetch = hasLoadedDesktopOnceRef.current
        const fetchStartedAt = Date.now()

        if (!isRefetch) {
            setIsLoadingInitialDesktop(true)
        }
        if (isRefetch) {
            desktopFetchInFlightRef.current += 1
            setShowDesktopRefetchLoader(true)
        }
        const requestId = ++desktopRequestIdRef.current

        function completeDesktopRefetchSlot() {
            desktopFetchInFlightRef.current = Math.max(0, desktopFetchInFlightRef.current - 1)
            if (desktopFetchInFlightRef.current === 0) {
                setShowDesktopRefetchLoader(false)
            }
        }

        ;api.getManifests(
            getPage(),
            getPerPage(),
            "created_at",
            "desc",
            getSearch(),
            /** @type {any} */ (getLocationQuery()),
            getYear()
        )
            .then((body) => {
                if (requestId !== desktopRequestIdRef.current) {
                    if (isRefetch) {
                        completeDesktopRefetchSlot()
                    }
                    return
                }
                const mappedManifests = (body?.data ?? []).map((manifest) => (
                    mapManifestForList(manifest, locations)
                ))
                const total = body?.total ?? 0
                if (!isRefetch) {
                    setManifests(mappedManifests)
                    setTotalRows(total)
                    return
                }
                const delay = refetchRemainingMs(fetchStartedAt)
                window.setTimeout(() => {
                    if (requestId !== desktopRequestIdRef.current) {
                        completeDesktopRefetchSlot()
                        return
                    }
                    setManifests(mappedManifests)
                    setTotalRows(total)
                    completeDesktopRefetchSlot()
                }, delay)
            })
            .catch((e) => {
                if (requestId !== desktopRequestIdRef.current) {
                    if (isRefetch) {
                        completeDesktopRefetchSlot()
                    }
                    return
                }
                console.error("Failed to load v2 manifests:", e)
                if (!isRefetch) {
                    return
                }
                const delay = refetchRemainingMs(fetchStartedAt)
                window.setTimeout(() => {
                    if (requestId !== desktopRequestIdRef.current) {
                        completeDesktopRefetchSlot()
                        return
                    }
                    completeDesktopRefetchSlot()
                }, delay)
            })
            .finally(() => {
                if (requestId !== desktopRequestIdRef.current) {
                    return
                }
                if (!hasLoadedDesktopOnceRef.current) {
                    hasLoadedDesktopOnceRef.current = true
                    setIsLoadingInitialDesktop(false)
                }
            })
    }, [app, isMobile, pageParam, perPageParam, fitPerPage, searchParam, locationParam, yearParam])

    return (
        <V2PageContainer>
            <Toast {...toastProps} />
            <ManifestsHeader
                showImportLodgeManifestWorkflow={showImportLodgeManifestWorkflow}
                onImportLodgeManifestClick={() => showNotImplementedToast()}
            />
            <ManifestsFilters
                showAllLocationsBadge={showAllLocationsBadge}
                showCurrentYearFilter={showCurrentYearFilter}
                currentYearLabel={getCurrentYear()}
                searchTerm={searchTerm}
                onSearchTermChange={setSearchTerm}
                onSearchSubmit={handleSearchSubmit}
                onRefresh={onRefreshClick}
                onToggleAllLocations={onToggleAllLocations}
                isAllLocations={(locationParam ?? "").toLowerCase() === "all"}
                onToggleCurrentYear={onToggleCurrentYear}
                isCurrentYear={String(getYear()) === getCurrentYear()}
            />
            {!isMobile ? (
                <div className="flex-grow-1 d-flex flex-column">
                    <ManifestsDesktopTable
                        manifests={manifests}
                        isLoadingInitial={isLoadingInitialDesktop}
                        totalRows={totalRows}
                        page={getPage()}
                        perPage={getPerPage()}
                        onPageChange={handlePageChange}
                        onPerPageChange={handlePerPageChange}
                        perPageSelection={isPerPageFit() ? "fit" : getPerPage()}
                        onFitPerPageChange={handleFitPerPageChange}
                        onManifestClick={handleManifestClick}
                        showRefetchLoader={showDesktopRefetchLoader}
                        manifestStatusColor={manifestStatusColor}
                        onSelectionChange={setSelectedManifests}
                        columns={columns}
                    />
                </div>
            ) : (
                <>
                    <div className="mb-3">
                        <p className="text-secondary mb-0" style={{ fontSize: "0.875rem" }}>
                            Showing {mobileManifestsList.total} manifests
                        </p>
                    </div>
                    <ManifestsMobileList
                        manifests={mobileManifestsList.items}
                        hasMore={mobileManifestsList.hasMore}
                        isLoading={mobileManifestsList.isLoading}
                        isLoadingInitial={mobileManifestsList.isLoadingInitial}
                        onLoadMore={mobileManifestsList.loadMore}
                        onManifestClick={handleManifestClick}
                        manifestStatusColor={/** @type {any} */ (manifestStatusColor)}
                        selectedManifests={selectedManifests}
                        onSelectedManifestsChange={setSelectedManifests}
                    />
                </>
            )}
            <ScrollToTopButton className="d-lg-none" />
        </V2PageContainer>
    )
}

